/**
 * Funnel telemetry for the widget.
 *
 * Deliberately not an analytics SDK. The widget runs on other people's
 * websites, so this ships no third-party script, sets no cookie, writes to no
 * storage and reads nothing identifying. Each page view gets a random id that
 * lives in memory and dies with the tab — enough to join "opened" to "called"
 * on one visit, and useless for anything beyond it.
 *
 * Events are posted to our own origin and turned into captures server-side, so
 * the analytics key never reaches a customer page.
 *
 * Without this the funnel was invisible: calls were recorded once they
 * happened, but how many visitors saw the widget, opened it, or dropped out
 * before speaking was unmeasurable — and so was any change we made to it.
 */

import { logger } from "../utils/logger.js";

type WidgetEventName =
  /** The widget booted and is about to be visible. One per page view. */
  | "web_widget_loaded"
  | "web_widget_opened"
  | "web_widget_nudge_shown"
  /** A call or chat is connected, not merely requested. */
  | "web_widget_conversation_started"
  | "web_widget_conversation_ended"
  | "web_widget_conversation_failed";

type WidgetEventProps = Record<string, string | number | boolean>;

/** A closed set, so a dashboard breaks failures down without a long tail of server codes. */
type FailureReason = "config" | "auth" | "network" | "sdk" | "limit" | "unknown";

const FAILURE_REASONS: Record<string, FailureReason> = {
  agent_not_found: "config",
  agent_not_deployed: "config",
  invalid_request: "config",
  invalid_dynamic_variables: "config",
  variable_schema_unavailable: "config",
  origin_not_allowed: "auth",
  modality_not_allowed: "auth",
  rate_limited: "limit",
  quota_exhausted: "limit",
  network_error: "network",
  // Browser and SDK side: nothing the server said.
  conversation_error: "sdk",
  webrtc_unsupported: "sdk",
  mic_permission_denied: "sdk",
  mic_not_found: "sdk",
  mic_unavailable: "sdk",
};

/** Props of `web_widget_conversation_failed`: the closed reason, and the raw code beside it. */
export function failureProps(code: string): WidgetEventProps {
  return { reason: FAILURE_REASONS[code] ?? "unknown", error_code: code.slice(0, 80) };
}

export class Telemetry {
  /** Per page view, in memory only. Never persisted, never reused across tabs. */
  private readonly viewId = createUuid();
  /** How many times each event went out in this view: it names the logical event. */
  private readonly sent = new Map<WidgetEventName, number>();
  private readonly endpoint: string;

  constructor(
    private readonly agentId: string,
    serverUrl: string,
  ) {
    this.endpoint = `${serverUrl.replace(/\/+$/, "")}/api/embed/events`;
  }

  /**
   * Fire and forget. Never throws and never blocks the UI: a blocked request,
   * an ad blocker or an offline visitor must not cost anyone a call.
   *
   * No-ops without an agent id, which is what the in-builder preview looks
   * like — an operator styling a draft is not a visitor and must not enter the
   * funnel.
   */
  track(event: WidgetEventName, props?: WidgetEventProps): void {
    if (!this.agentId) return;

    const count = (this.sent.get(event) ?? 0) + 1;
    this.sent.set(event, count);
    try {
      void fetch(`${this.endpoint}?agentId=${encodeURIComponent(this.agentId)}`, {
        method: "POST",
        credentials: "omit",
        // Survives the tab closing right after the visitor hangs up, which is
        // exactly when the most interesting event of the funnel fires.
        keepalive: true,
        // text/plain keeps this a CORS "simple request", so the browser skips
        // the preflight entirely. The body is still JSON and the server parses
        // it as such; the header is about the round trip, not the format. An
        // extra OPTIONS per funnel event would double the request count for no
        // gain, since we never read the response.
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: JSON.stringify({
          viewId: this.viewId,
          // The same logical event always has the same id, so a resend dedupes.
          eventId: eventUuid(`${this.viewId}:${event}:${count}`),
          ts: Date.now(),
          event,
          props: props ?? {},
        }),
      }).catch((error) => logger.debug("telemetry post failed", event, error));
    } catch (error) {
      logger.debug("telemetry threw", event, error);
    }
  }
}

/**
 * A v4-shaped uuid derived from `name` (the relay only accepts uuids); SubtleCrypto is async
 * and secure-context only, so this is a small synchronous 128-bit mix (cyrb128).
 */
function eventUuid(name: string): string {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < name.length; i++) {
    const k = name.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  const hex = [h1, h2, h3, h4].map((n) => (n >>> 0).toString(16).padStart(8, "0")).join("");
  const variant = ((Number.parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

/**
 * `crypto.randomUUID` is unavailable outside secure contexts and on older
 * Safari, and an id we cannot generate must not take the widget down with it.
 */
function createUuid(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the arithmetic fallback
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const digit = Math.floor(Math.random() * 16);
    return (char === "x" ? digit : (digit & 0x3) | 0x8).toString(16);
  });
}
